import { cloneElement, Fragment, isValidElement, useCallback, useState, type ReactElement, type ReactNode } from "react";
import { Caption1, Checkbox, makeStyles, mergeClasses, tokens, Tooltip } from "@fluentui/react-components";
import { Add16Regular, ChevronDown12Regular, Dismiss12Regular, Filter16Regular } from "@fluentui/react-icons";
import { FuiButton, FuiDropdown } from "@/components/fui-control";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { DUE_FILTER_OPTIONS } from "@/features/tasks/task-filter-chips";
import { priorityGlyph, statusGlyph, taskFieldIcon } from "@/features/tasks/task-glyphs";
import { LABEL_DOT_COLOR } from "@/features/tasks/task-ui";
import type { LabelColor } from "@/engine/types";
import { PRIORITY_LABEL, PRIORITY_ORDER, STATUS_LABEL, STATUS_ORDER } from "@/store/execution-task-ops";

export type FilterOp = "is" | "isNot";

export interface FilterChoice {
  id: string;
  label: string;
  icon?: ReactNode;
}

export interface FilterFieldDef {
  id: string;
  label: string;
  icon: ReactNode;
  choices: FilterChoice[];
}

export interface FilterClause {
  id: string;
  fieldId: string;
  op: FilterOp;
  values: string[];
}

const EMPTY_COPY = "적용된 조건이 없습니다. 상태를 진행 중으로, 담당을 한 사람으로 좁혀 보세요.";

const OP_OPTIONS: Array<{ value: FilterOp; label: string }> = [
  { value: "is", label: "이다" },
  { value: "isNot", label: "아니다" },
];

export function filterOpLabel(op: FilterOp): string {
  return op === "isNot" ? "아니다" : "이다";
}

/** 칩에 보이는 값. 없으면 전체, 하나면 그 이름, 둘이면 나열, 셋 이상이면 개수. */
export function filterClauseText(labels: readonly string[]): string {
  if (labels.length === 0) return "전체";
  if (labels.length === 1) return labels[0] ?? "전체";
  if (labels.length === 2) return `${labels[0]}, ${labels[1]}`;
  return `${labels.length}개`;
}

/** 고른 값을 필드 선택지 순서로 읽는다. */
export function filterChoiceLabels(clause: FilterClause, field: FilterFieldDef | undefined): string[] {
  if (!field) return [...clause.values];
  const picked = new Set(clause.values);
  const known = new Set(field.choices.map((choice) => choice.id));
  const ordered = field.choices.filter((choice) => picked.has(choice.id)).map((choice) => choice.label);
  const rest = clause.values.filter((id) => !known.has(id));
  return [...ordered, ...rest];
}

export function createFilterClause(fieldId: string): FilterClause {
  return { id: crypto.randomUUID(), fieldId, op: "is", values: [] };
}

function paint(icon: ReactNode): ReactNode {
  return isValidElement(icon) ? cloneElement(icon as ReactElement) : icon;
}

const useStyles = makeStyles({
  bar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalSNudge,
    minWidth: 0,
  },
  lead: {
    display: "inline-flex",
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  conj: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    maxWidth: "280px",
    height: "24px",
    paddingLeft: "6px",
    paddingRight: "2px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    minWidth: 0,
  },
  chipIcon: {
    display: "inline-flex",
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  chipText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
  },
  field: {
    color: tokens.colorNeutralForeground3,
  },
  op: {
    color: tokens.colorNeutralForeground3,
  },
  value: {
    color: tokens.colorNeutralForeground1,
  },
  dismiss: {
    minWidth: "20px",
    width: "20px",
    height: "20px",
    maxWidth: "20px",
    padding: 0,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  spacer: {
    flexGrow: 1,
    flexBasis: "8px",
    minWidth: "8px",
  },
  panel: {
    width: "100%",
    maxWidth: "560px",
    minWidth: 0,
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "30px",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "8px",
    paddingRight: "4px",
    gap: tokens.spacingHorizontalS,
  },
  headLabel: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
  },
  empty: {
    margin: 0,
    paddingTop: "2px",
    paddingBottom: "8px",
    paddingLeft: "8px",
    paddingRight: "8px",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
  },
  rows: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingHorizontalSNudge,
    paddingTop: "2px",
    paddingBottom: "6px",
    paddingLeft: "8px",
    paddingRight: "8px",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "48px minmax(120px, 148px) 84px minmax(0, 1fr) 24px",
    gap: tokens.spacingHorizontalSNudge,
    alignItems: "center",
  },
  rowConj: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    textAlign: "right",
    paddingRight: "4px",
    whiteSpace: "nowrap",
  },
  valueBtn: {
    height: "24px",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalSNudge,
    paddingLeft: "8px",
    paddingRight: "8px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  valueText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    flexGrow: 1,
    textAlign: "left",
  },
  valueEmpty: {
    color: tokens.colorNeutralForeground3,
  },
  chevron: {
    display: "inline-flex",
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  choices: {
    marginLeft: "54px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: "4px",
    maxHeight: "200px",
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
  },
  choice: {
    borderRadius: tokens.borderRadiusMedium,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  checkbox: {
    width: "100%",
    height: "24px",
    minHeight: "24px",
    maxHeight: "24px",
    alignItems: "center",
    paddingLeft: "4px",
    paddingRight: "4px",
  },
  checkboxIndicator: {
    alignSelf: "center",
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
  },
  checkboxLabel: {
    display: "inline-flex",
    alignItems: "center",
    alignSelf: "center",
    gap: tokens.spacingHorizontalSNudge,
    height: "16px",
    marginTop: 0,
    marginBottom: 0,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: "6px",
    paddingRight: 0,
    fontSize: tokens.fontSizeBase300,
    lineHeight: "16px",
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  choiceIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
    flexShrink: 0,
  },
  sep: {
    height: "1px",
    backgroundColor: tokens.colorNeutralStroke2,
    marginTop: "4px",
    marginBottom: "4px",
  },
  add: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "4px",
    paddingTop: "4px",
    paddingBottom: "6px",
    paddingLeft: "8px",
    paddingRight: "8px",
  },
  addLabel: {
    marginRight: "4px",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    height: "24px",
    paddingLeft: "6px",
    paddingRight: "8px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  badgeIcon: {
    display: "inline-flex",
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  swatch: {
    width: "8px",
    height: "8px",
    borderRadius: "2px",
    flexShrink: 0,
  },
});

function ChoiceSwatch({ color }: { color: string }) {
  const styles = useStyles();
  return <span className={styles.swatch} style={{ backgroundColor: color }} />;
}

const SAMPLE_LABELS: Array<{ id: string; name: string; color: LabelColor }> = [
  { id: "l_growth", name: "그로스", color: "purple" },
  { id: "l_crm", name: "CRM", color: "blue" },
  { id: "l_copy", name: "카피", color: "orange" },
  { id: "l_data", name: "데이터", color: "teal" },
];

/** 작업 필터와 같은 필드. 담당·버킷·라벨·WBS는 디자인 시스템 예시용 이름이다. */
export function taskFilterFields(): FilterFieldDef[] {
  return [
    {
      id: "status",
      label: "상태",
      icon: taskFieldIcon("status"),
      choices: [
        { id: "unset", label: "상태 미정", icon: statusGlyph(undefined, 16, true) },
        ...STATUS_ORDER.map((status) => ({ id: status, label: STATUS_LABEL[status], icon: statusGlyph(status, 16, true) })),
      ],
    },
    {
      id: "priority",
      label: "우선순위",
      icon: taskFieldIcon("priority"),
      choices: [
        { id: "unset", label: "우선순위 미정", icon: priorityGlyph(undefined) },
        ...PRIORITY_ORDER.map((priority) => ({ id: priority, label: PRIORITY_LABEL[priority], icon: priorityGlyph(priority) })),
      ],
    },
    {
      id: "due",
      label: "마감",
      icon: taskFieldIcon("due"),
      choices: DUE_FILTER_OPTIONS.filter((option) => option.value !== "any").map((option) => ({
        id: option.value,
        label: option.label,
      })),
    },
    {
      id: "assignee",
      label: "담당",
      icon: taskFieldIcon("assignee"),
      choices: [
        { id: "unset", label: "담당 미정", icon: <PersonAvatar name="담당 미정" size={16} tip={false} /> },
        { id: "me", label: "나", icon: <PersonAvatar name="나" size={16} tip={false} /> },
        { id: "kim", label: "김PM", icon: <PersonAvatar name="김PM" size={16} tip={false} /> },
        { id: "lee", label: "이아키", icon: <PersonAvatar name="이아키" size={16} tip={false} /> },
      ],
    },
    {
      id: "bucket",
      label: "버킷",
      icon: taskFieldIcon("bucket"),
      choices: [
        { id: "none", label: "버킷 없음" },
        { id: "b_backlog", label: "아이디어" },
        { id: "b_now", label: "이번 스프린트" },
        { id: "b_review", label: "실험 대기" },
      ],
    },
    {
      id: "label",
      label: "라벨",
      icon: taskFieldIcon("label"),
      choices: SAMPLE_LABELS.map((label) => ({
        id: label.id,
        label: label.name,
        icon: <ChoiceSwatch color={LABEL_DOT_COLOR[label.color]} />,
      })),
    },
    {
      id: "scope",
      label: "WBS",
      icon: taskFieldIcon("scope"),
      choices: [
        { id: "w1", label: "1 착수" },
        { id: "w2", label: "1.1 현황 분석" },
        { id: "w3", label: "2 설계" },
      ],
    },
  ];
}

export function FilterChipBar({
  fields,
  clauses,
  onRemove,
  onClear,
  onAdd,
  add,
}: {
  fields: FilterFieldDef[];
  clauses: FilterClause[];
  onRemove: (id: string) => void;
  onClear: () => void;
  onAdd?: () => void;
  add?: ReactNode;
}) {
  const styles = useStyles();
  if (clauses.length === 0) return null;
  const byId = new Map(fields.map((field) => [field.id, field]));
  const addControl = add ?? (
    <FuiButton appearance="subtle" density="dialog" icon={<Add16Regular />} onClick={onAdd}>
      추가
    </FuiButton>
  );
  return (
    <div className={styles.bar} role="group" aria-label="적용된 필터">
      <span className={styles.lead} aria-hidden>
        <Filter16Regular />
      </span>
      {clauses.map((clause, index) => {
        const field = byId.get(clause.fieldId);
        const label = field?.label ?? clause.fieldId;
        const summary = filterClauseText(filterChoiceLabels(clause, field));
        const op = filterOpLabel(clause.op);
        const full = `${label} ${op} ${summary}`;
        return (
          <Fragment key={clause.id}>
            {index > 0 ? <span className={styles.conj}>그리고</span> : null}
            <span className={styles.chip}>
              <span className={styles.chipIcon}>{paint(field?.icon)}</span>
              <Tooltip content={full} relationship="description">
                <span className={styles.chipText}>
                  <span className={styles.field}>{label}</span> <span className={styles.op}>{op}</span> <span className={styles.value}>{summary}</span>
                </span>
              </Tooltip>
              <FuiButton
                className={styles.dismiss}
                appearance="transparent"
                density="dialog"
                icon={<Dismiss12Regular />}
                aria-label={`${label} 필터 지우기`}
                onClick={() => onRemove(clause.id)}
              />
            </span>
          </Fragment>
        );
      })}
      {addControl}
      <span className={styles.spacer} />
      <FuiButton appearance="subtle" density="dialog" onClick={onClear}>
        모두 지우기
      </FuiButton>
    </div>
  );
}

export function FilterBuilder({
  fields,
  clauses,
  onChange,
  openClauseId,
  onOpenClauseId,
}: {
  fields: FilterFieldDef[];
  clauses: FilterClause[];
  onChange: (next: FilterClause[]) => void;
  openClauseId?: string | null;
  onOpenClauseId?: (id: string | null) => void;
}) {
  const styles = useStyles();
  const [mountNode, setMountNode] = useState<HTMLDivElement | null>(null);
  const setRoot = useCallback((node: HTMLDivElement | null) => {
    setMountNode((current) => (current === node ? current : node));
  }, []);
  const [uncontrolledOpen, setUncontrolledOpen] = useState<string | null>(null);
  const controlled = openClauseId !== undefined;
  const openId = controlled ? openClauseId : uncontrolledOpen;
  const setOpen = (id: string | null) => {
    onOpenClauseId?.(id);
    if (!controlled) setUncontrolledOpen(id);
  };
  const fieldOptions = fields.map((field) => ({ value: field.id, label: field.label }));
  const byId = new Map(fields.map((field) => [field.id, field]));

  return (
    <div
      className={styles.panel}
      role="group"
      aria-label="필터"
      ref={setRoot}
    >
      <div className={styles.head}>
        <Caption1 className={styles.headLabel}>필터</Caption1>
        {clauses.length > 0 ? (
          <FuiButton appearance="subtle" density="dialog" onClick={() => { setOpen(null); onChange([]); }}>
            모두 지우기
          </FuiButton>
        ) : null}
      </div>
      {clauses.length === 0 ? (
        <p className={styles.empty}>{EMPTY_COPY}</p>
      ) : (
        <div className={styles.rows}>
          {clauses.map((clause, index) => {
            const field = byId.get(clause.fieldId);
            const labels = filterChoiceLabels(clause, field);
            const empty = labels.length === 0;
            const valueText = empty ? "선택" : filterClauseText(labels);
            const open = openId === clause.id;
            return (
              <div key={clause.id}>
                <div className={styles.row}>
                  <span className={styles.rowConj}>{index === 0 ? "조건" : "그리고"}</span>
                  <FuiDropdown
                    density="dialog"
                    aria-label="필드"
                    value={clause.fieldId}
                    options={fieldOptions}
                    mountNode={mountNode ?? undefined}
                    onValueChange={(fieldId) => onChange(clauses.map((item) => (item.id === clause.id ? { ...item, fieldId, values: [] } : item)))}
                  />
                  <FuiDropdown
                    density="dialog"
                    aria-label="연산"
                    value={clause.op}
                    options={OP_OPTIONS}
                    mountNode={mountNode ?? undefined}
                    onValueChange={(op) => onChange(clauses.map((item) => (item.id === clause.id ? { ...item, op: op as FilterOp } : item)))}
                  />
                  <button
                    type="button"
                    className={styles.valueBtn}
                    aria-expanded={open}
                    aria-label={`${field?.label ?? clause.fieldId} 값`}
                    title={valueText}
                    onClick={() => setOpen(open ? null : clause.id)}
                  >
                    <span className={mergeClasses(styles.valueText, empty && styles.valueEmpty)}>{valueText}</span>
                    <span className={styles.chevron} aria-hidden>
                      <ChevronDown12Regular />
                    </span>
                  </button>
                  <FuiButton
                    className={styles.dismiss}
                    appearance="transparent"
                    density="dialog"
                    icon={<Dismiss12Regular />}
                    aria-label={`${field?.label ?? clause.fieldId} 조건 지우기`}
                    onClick={() => {
                      if (open) setOpen(null);
                      onChange(clauses.filter((item) => item.id !== clause.id));
                    }}
                  />
                </div>
                {open && field ? (
                  <div className={styles.choices} role="group" aria-label={`${field.label} 값`}>
                    {field.choices.map((choice) => (
                      <div key={choice.id} className={styles.choice}>
                        <Checkbox
                          className={styles.checkbox}
                          indicator={{ className: styles.checkboxIndicator }}
                          checked={clause.values.includes(choice.id)}
                          onChange={(_, data) => {
                            const has = clause.values.includes(choice.id);
                            const values = data.checked ? (has ? clause.values : [...clause.values, choice.id]) : clause.values.filter((id) => id !== choice.id);
                            onChange(clauses.map((item) => (item.id === clause.id ? { ...item, values } : item)));
                          }}
                          label={{
                            className: styles.checkboxLabel,
                            children: (
                              <>
                                {choice.icon ? <span className={styles.choiceIcon}>{paint(choice.icon)}</span> : null}
                                {choice.label}
                              </>
                            ),
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      <div className={styles.sep} />
      <div className={styles.add}>
        <span className={styles.addLabel}>필터 추가</span>
        {fields.map((field) => (
          <button key={field.id} type="button" className={styles.badge} onClick={() => onChange([...clauses, createFilterClause(field.id)])}>
            <span className={styles.badgeIcon}>{paint(field.icon)}</span>
            {field.label}
          </button>
        ))}
      </div>
    </div>
  );
}
